// Simple Rooms API client
// Uses base URL from Vite env: VITE_BACKEND_URL
// Endpoints (assumed):
//   GET   {BASE}/api/rooms/:roomId  → 200 with JSON body, ETag in header, 404 if not found
//   PUT   {BASE}/api/rooms/:roomId  → 200/204 with ETag in header
//   PATCH {BASE}/api/rooms/:roomId  → 200/204 with ETag in header

// Import constants
import { BASE_URL } from '../constants/env';

export type RoomDocument<T> = {
  payload: T;
  etag?: string;
  deviceId?: string;
  createdAt?: string; // ISO string from X-Created-At
  updatedAt?: string; // ISO string from X-Updated-At
  lastAccessAt?: string; // ISO string from X-Last-Access-At
};

export type ApiError = Error & { status?: number };

export async function getRoom<T = unknown>(
  roomId: string,
  deviceId?: string,
): Promise<RoomDocument<T>> {
  const url = BASE_URL + '/api/rooms/' + encodeURIComponent(roomId);

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...(deviceId ? { 'X-Device-Id': deviceId } : {}),
    },
  });

  if (res.status === 404) {
    const err = new Error('Not Found') as ApiError;
    err.status = 404;
    throw err;
  }

  if (!res.ok) {
    const errText = await safeText(res);
    const err = new Error(
      'GET room failed: ' + res.status + ' ' + errText,
    ) as ApiError;
    err.status = res.status;
    throw err;
  }

  const etag = res.headers.get('ETag') || undefined;
  const _deviceId = res.headers.get('X-Device-Id') || undefined;
  const createdAt = res.headers.get('X-Created-At') || undefined;
  const updatedAt = res.headers.get('X-Updated-At') || undefined;
  const lastAccessAt = res.headers.get('X-Last-Access-At') || undefined;
  const json = (await res.json()) as T;
  return {
    payload: json,
    etag,
    deviceId: _deviceId,
    createdAt,
    updatedAt,
    lastAccessAt,
  };
}

export async function putRoom<T = unknown>(
  roomId: string,
  payload: T,
  opts?: { etag?: string; deviceId?: string },
): Promise<{
  etag?: string;
  deviceId?: string;
  createdAt?: string;
  updatedAt?: string;
  lastAccessAt?: string;
}> {
  const url = BASE_URL + '/api/rooms/' + encodeURIComponent(roomId);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts?.deviceId ? { 'X-Device-Id': opts.deviceId } : {}),
    ...(opts?.etag ? { 'If-Match': opts.etag } : {}),
  };

  const res = await fetch(url, {
    method: 'PUT',
    headers: headers,
    body: JSON.stringify(payload),
  });

  if (res.status === 412) {
    const err = new Error('Precondition Failed') as ApiError;
    err.status = 412;
    throw err;
  }

  if (!res.ok) {
    const errText = await safeText(res);
    const err = new Error(
      'PUT room failed: ' + res.status + ' ' + errText,
    ) as ApiError;
    err.status = res.status;
    throw err;
  }

  const etag = res.headers.get('ETag') || undefined;
  const deviceId = res.headers.get('X-Device-Id') || undefined;
  const createdAt = res.headers.get('X-Created-At') || undefined;
  const updatedAt = res.headers.get('X-Updated-At') || undefined;
  const lastAccessAt = res.headers.get('X-Last-Access-At') || undefined;
  return { etag, deviceId, createdAt, updatedAt, lastAccessAt };
}

export async function patchRoom<T = unknown>(
  roomId: string,
  delta: Partial<T>,
  opts?: { etag?: string; deviceId?: string },
): Promise<{
  etag?: string;
  deviceId?: string;
  createdAt?: string;
  updatedAt?: string;
  lastAccessAt?: string;
}> {
  const url = BASE_URL + '/api/rooms/' + encodeURIComponent(roomId);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts?.deviceId ? { 'X-Device-Id': opts.deviceId } : {}),
    ...(opts?.etag ? { 'If-Match': opts.etag } : {}),
  };

  const res = await fetch(url, {
    method: 'PATCH',
    headers: headers,
    body: JSON.stringify(delta),
  });

  if (res.status === 412) {
    const err = new Error('Precondition Failed') as ApiError;
    err.status = 412;
    throw err;
  }

  if (res.status === 404) {
    const err = new Error('Not Found') as ApiError;
    err.status = 404;
    throw err;
  }

  if (!res.ok) {
    const errText = await safeText(res);
    const err = new Error(
      'PATCH room failed: ' + res.status + ' ' + errText,
    ) as ApiError;
    err.status = res.status;
    throw err;
  }

  const etag = res.headers.get('ETag') || undefined;
  const deviceId = res.headers.get('X-Device-Id') || undefined;
  const createdAt = res.headers.get('X-Created-At') || undefined;
  const updatedAt = res.headers.get('X-Updated-At') || undefined;
  const lastAccessAt = res.headers.get('X-Last-Access-At') || undefined;
  return { etag, deviceId, createdAt, updatedAt, lastAccessAt };
}

async function safeText(res: Response): Promise<string> {
  try {
    return await res.text();
  } catch {
    return '';
  }
}
